import {css, StyleDeclarationValue, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {Section} from 'constants/NavConstants';
import {isNavbarColored} from 'nav/navbar/NavBarColor';
import {sectionToReadable} from 'nav/NavUtil';
import hoverAndActiveStyles from 'styles/hoverAndActiveStyles';
import {ITEM_SIZE_SIDE_NAVBAR} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {snakeToCamelCase} from 'util/NameUtil';
import {ElementMouseEvent} from 'util/Types';

// aphrodite doesn't export this for some reason
type CSSInputTypes = StyleDeclarationValue | false | null | void;

export function getIconStyles(isActive: boolean): Array<CSSInputTypes> {
  return [
    isNavbarColored() ? navBarIconStyles.coloredItem : navBarIconStyles.item,
    isActive && (isNavbarColored() ? navBarIconStyles.coloredActive : navBarIconStyles.active),
    window.impersonating && navBarIconStyles.impersonating,
    window.impersonating && isActive && navBarIconStyles.impersonatingActive,
  ];
}

export interface NavBarIconProps {
  className?: string;
  section: Section;
  icon: BenchlingIconName;
  isActive: boolean;
  link?: string;
  onSectionClick?: (e: ElementMouseEvent, section: Section) => void;
  hasActivity: boolean;
}

class NavBarIcon extends React.Component<NavBarIconProps> {
  static defaultProps = {
    hasActivity: false,
    isActive: false,
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleSectionClick = (e: ElementMouseEvent) => {
    if (this.props.link && this.props.onSectionClick) {
      // If both a link and click handler are provided, we want the click handler behavior to override
      // the default link action
      e.preventDefault();
    }
    if (this.props.onSectionClick) {
      this.props.onSectionClick(e, this.props.section);
    }
  };

  render(): JSX.Element {
    const showActivityIndicator = this.props.hasActivity && !this.props.isActive;
    const innerIcon = (
      <div className={css(navBarIconStyles.inner)}>
        <BenchlingIcon
          className={classNames(css(navBarIconStyles.icon), this.props.className)}
          icon={this.props.icon}
        />
        {showActivityIndicator && (
          <div
            className={classNames(
              css(navBarIconStyles.activityIndicator),
              '_test-navBarIcon-activityIndicator'
            )}
          />
        )}
      </div>
    );
    const itemStyles = getIconStyles(this.props.isActive);
    return (
      <Tooltip
        container="body"
        disabled={this.props.isActive}
        placement={TooltipPlacement.RIGHT}
        title={sectionToReadable(this.props.section)}
      >
        {(() => {
          const dataTestAttrs = {
            component: 'NavBarIcon',
            key: snakeToCamelCase(this.props.section.toLowerCase()),
          };

          if (this.props.link) {
            return (
              <a
                href={this.props.link}
                onClick={this._handleSectionClick}
                className={css(itemStyles)}
                {...getTestProps(dataTestAttrs)}
              >
                {innerIcon}
              </a>
            );
          }
          return (
            <div
              onClick={this._handleSectionClick}
              className={css(itemStyles)}
              {...getTestProps(dataTestAttrs)}
            >
              {innerIcon}
            </div>
          );
        })()}
      </Tooltip>
    );
  }
}

export default NavBarIcon;

export const hoverStyles = {
  backgroundColor: Colors.BLUE_DARKER,
  color: Colors.WHITE,
  textDecoration: 'none',
};
const impersonatingHoverStyles = {
  backgroundColor: Colors.RED_DARK,
};
const coloredNavbarActiveStyles = {
  backgroundColor: 'rgba(0,0,0,0.1)',
};
export const navBarIconStyles = StyleSheet.create({
  coloredItem: {
    color: Colors.GRAY_LIGHT,
    cursor: 'pointer',
    display: 'block',
    height: ITEM_SIZE_SIDE_NAVBAR,
    position: 'relative',
    ...hoverAndActiveStyles(coloredNavbarActiveStyles),
  },
  item: {
    color: Colors.GRAY_LIGHT,
    cursor: 'pointer',
    display: 'block',
    height: ITEM_SIZE_SIDE_NAVBAR,
    position: 'relative',
    ...hoverAndActiveStyles(hoverStyles),
  },
  active: hoverStyles,
  coloredActive: coloredNavbarActiveStyles,
  impersonating: hoverAndActiveStyles(impersonatingHoverStyles),
  impersonatingActive: impersonatingHoverStyles,
  icon: {
    fontSize: 14,
  },
  inner: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
  activityIndicator: {
    borderColor: Colors.GREEN_LIGHT,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 4,
    height: 8,
    width: 8,
    position: 'absolute',
    top: 23,
    right: 4,
  },
});
